import { useTranslation } from 'react-i18next'
import { Badge, BadgeVariant, CardContent, CardDashboard, Flex, IllustratedMessage } from '@percent/lemonade'
import { DonationDetailsProps, FormatAmountParams } from './DonationDetails.types'
import { CurrencyCode, formatMoney } from '@percent/utility'
import { PaymentStatus } from '@percent/cause-dashboard/services/grants/grantsService.types'
import { TFunction } from 'i18next'
import { format } from 'date-fns'

const formatAmount = ({ amount, currency, locale, t }: FormatAmountParams): string => {
  if (amount === undefined || amount === null || !currency) {
    return t('typography.notAvailable')
  }

  return formatMoney({ amount: Number(amount), currency: currency as CurrencyCode }, { locale })
}

const getPaymentStatusBadge = (
  status: PaymentStatus | undefined,
  t: TFunction
): { label: string; variant: BadgeVariant } => {
  switch (status) {
    case 'processing':
      return {
        label: t('grants.disbursements.status.pending'),
        variant: 'default'
      }
    case 'paid':
      return {
        label: t('grants.disbursements.status.paid'),
        variant: 'positive'
      }
    default:
      return {
        label: status ?? t('typography.notAvailable'),
        variant: 'default'
      }
  }
}

export function DonationDetails({ paymentDetails, award }: Readonly<DonationDetailsProps>) {
  const { t, i18n } = useTranslation()

  const isPaymentDetailsNotAvailable = !paymentDetails?.status

  const { label, variant } = getPaymentStatusBadge(paymentDetails?.status, t)

  return (
    <Flex direction="column" data-testid="organization-details-container">
      {isPaymentDetailsNotAvailable ? (
        <CardDashboard data-testid="no-payment-details-content">
          <Flex direction="column" align="center" justify="center" w="50%" mx="auto" my={100}>
            <IllustratedMessage
              title={t('grants.grantDetails.donationDetails.emptyScreen.title')}
              description={t('grants.grantDetails.donationDetails.emptyScreen.description')}
              illustration="no-payouts"
            />
          </Flex>
        </CardDashboard>
      ) : (
        <CardDashboard title={t('grants.grantDetails.donationDetails.title')} data-testid="payment-details-card">
          <CardContent title={t('grants.grantDetails.donationDetails.awardAmount')} testId="payment-amount-usd">
            {formatAmount({
              amount: award?.amount,
              currency: award?.currency,
              locale: i18n.language,
              t
            })}
          </CardContent>
          <CardContent title={t('grants.grantDetails.donationDetails.donationAmount')} testId="payment-amount-local">
            {formatAmount({
              amount: paymentDetails?.payment?.amount,
              currency: paymentDetails?.payment?.currency,
              locale: i18n.language,
              t
            })}
          </CardContent>
          <CardContent title={t('grants.grantDetails.donationDetails.donationStatus')} testId="payment-status">
            <Badge variant={variant}>{label}</Badge>
          </CardContent>
          <CardContent title={t('grants.grantDetails.donationDetails.donationDate')} testId="payment-date">
            {paymentDetails?.paidAt
              ? format(new Date(paymentDetails.paidAt), 'dd MMM yyyy, HH:mm')
              : t('typography.notAvailable')}
          </CardContent>
          <CardContent title={t('grants.grantDetails.donationDetails.disbursedBy')} testId="payment-disbursed-by">
            {paymentDetails?.disbursedBy ?? t('typography.notAvailable')}
          </CardContent>
        </CardDashboard>
      )}
    </Flex>
  )
}
